/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { useQueryParam, StringParam } from "use-query-params";

import { COMPONENTS } from "../../constants.ts";
import Link from "../link";
import SVG from "../svg";
import { authEnabled, login, logout, isAuthenticated, isAuthorized } from "../../utils/auth";
import Navigation from "./navigation";
import CtaNavigation from "./cta-navigation";
import SeoNavigation from "./seo-navigation";
import IntlMenu from "./intl-menu";
import ResourcesMenu from "./resources-menu";
import resources_menu_links from "./resources-menu/resourcesLinksData.js";
import UtilityNavigation from "./utility-navigation";

import * as Logos from "../svg/logos";

import * as styles from "./styles.module.scss";

const Header = (props) => {
  if (!props.data) {
    return <div></div>;
  }

  // ------------------------------------------------------ | Props

  const { name, navigation, site_logo, utility_nav, resource_menu } =
    props.data?.attributes || props;

  const resourceLinks =
    resource_menu.length > 0
      ? [
          {
            id: "resources-menu",
            page: null,
            component: "subnav-link",
            linkText: "Resources",
            subnav_links: resource_menu
          }
        ]
      : resources_menu_links;

  const [error] = useQueryParam("error", StringParam);
  const handleLogout = (e) => {
    logout();
    e.preventDefault();
  };

  const handleLogin = (e) => {
    login();
    e.preventDefault();
  };

  // ------------------------------------------------------ | Transform CTA data
  const [dataLoaded, setLoaded] = useState(false);
  const [cta_navigation, setCta] = useState([]);
  const [main_navigation, setMain] = useState([]);
  const [displayVersion] = useQueryParam("version", StringParam);
  const displayNav = displayVersion !== "print";

  const hasUtilityNav = !!utility_nav?.data || authEnabled();

  useEffect(() => {
    // Login
    if (authEnabled()) {
      let accountLinks = [];
      if (isAuthenticated()) {
        accountLinks.push(["Sign out", "#logout", handleLogout.bind(this)]);
      } else {
        accountLinks.push(["Sign in", "#login", handleLogin.bind(this)]);
      }

      accountLinks.forEach((el) => {
        const accountLink = {
          link_text: el[0],
          url: el[1],
          onClick: el[2],
          new_window: false,
          mobileOnly: hasUtilityNav
        };
        setCta([accountLink]);
      });
    }

    // Main navigation
    setMain(() => [
      // nav items
      ...(navigation || [])
        .filter(
          (item) =>
            item.__typename === COMPONENTS.SUBNAV_LINK ||
            item.__typename === COMPONENTS.LINK ||
            item.__typename === COMPONENTS.SUBNAV_LINK_WITH_CHILDREN
        )
        .map((item) => item)
    ]);

    // CTAs
    setCta((cta_navigation) => [
      ...(cta_navigation || []),
      ...(navigation || [])
        ?.filter((item) => item.__typename === COMPONENTS.INSERT_SNIPPET)
        .map((item) => item)
    ]);
    setLoaded(true);
  }, []);

  // ------------------------------------------------------ | Mobile Menu

  const [menuIsOpen, setMenu] = useState(false);

  const smallScreenMenuClick = () => {
    if (isMobileNavigation) {
      setMenu(!menuIsOpen);
      setLocked(!locked);
    }
  };

  // ------------------------------------------------------ | Lock Scrolling

  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (menuIsOpen === true) {
      document.documentElement.style.overflow = "hidden";
    } else if (locked === false || menuIsOpen === false) {
      document.documentElement.style.removeProperty("overflow");
    }
  }, [locked]);

  // ------------------------------------------------------ | Media Query

  const handleQueryChange = (match) => {
    if (!match) {
      setMenu(false);
      setLocked(false);
    }
  };

  const isMobileNavigation = useMediaQuery(
    { query: "(max-width: 1279px)" },
    undefined,
    handleQueryChange
  );

  // ------------------------------------------------------ | Classes

  const classes = classNames(`header`, styles.header, {
    [styles.navigation_is_showing]: menuIsOpen
  });

  const getLogo = (header_name) => {
    if (
      Object.keys(Logos || {}).includes(
        (header_name || "").replace(/(make)?[^a-zA-Z0-9]*/g, "").toLowerCase()
      )
    ) {
      header_name = `${header_name.split("_")[0]}-logo`;
      return <SVG name={header_name} />;
    } else if (site_logo) {
      return <img src={site_logo?.small?.url} alt="" />;
    }
  };

  // ------------------------------------------------------ | Search

  // const triggerSearch = () => {
  //   return true;
  // };

  // const searchButton = (
  //   <div
  //     key="search-button"
  //     className={styles.search_button}
  //     onClick={triggerSearch}
  //     onKeyDown={triggerSearch}
  //     tabIndex="0"
  //     role="button"
  //   >
  //     <SVG name="search" />
  //   </div>
  // );

  // ------------------------------------------------------ | Header component

  return !dataLoaded ? (
    <div
      className={classNames(styles.header_outer_container, {
        [styles.has_util]: hasUtilityNav
      })}
    ></div>
  ) : (
    <>
      {hasUtilityNav && (
        <UtilityNavigation
          featured_media={utility_nav?.data?.attributes?.featured_media}
          links={utility_nav?.data?.attributes?.links}
          hasAuth={authEnabled()}
        />
      )}
      <header className={classes}>
        {error && (
          <div>
            <div className={classNames(styles.alert, `alert`)}>
              <strong>NOTE:</strong> Having trouble accessing our secure resources? Please ensure
              your browser is configured to allow 3rd party cookies.{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://akohubteam.medium.com/how-to-enable-third-party-cookies-on-your-browsers-f9a8143b8cc5"
              >
                Learn more
              </a>
              .
            </div>
          </div>
        )}
        <SeoNavigation mainLinks={main_navigation} />
        <div className={classNames(`header_container`, styles.header_container)}>
          <div className={classNames(`logo_container`)}>
            <div className={classNames(`logo_container_content`, styles.logo_container_content)}>
              <Link className={classNames(`logo`, styles.logo)} to="/">
                {getLogo(name)}
              </Link>
              {displayNav && isMobileNavigation && (
                <button
                  className={classNames(`menu_button`, styles.menu_button)}
                  onClick={smallScreenMenuClick}
                >
                  <span>Menu</span>
                  {!menuIsOpen ? <SVG name="bars" /> : <SVG name="close" />}
                </button>
              )}
            </div>
          </div>

          {displayNav && (
            <div className={classNames(`navigation_container`, styles.navigation_container)}>
              {navigation && (
                <>
                  <Navigation
                    className={classNames(`main_navigation`, styles.main_navigation)}
                    links={main_navigation}
                    onClick={smallScreenMenuClick}
                  />

                  {/* --------------------------------------------- | Desktop CTAs  */}
                  {displayNav && !isMobileNavigation && (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      {/* {searchButton} */}
                      {authEnabled() && isAuthenticated() && isAuthorized && (
                        <ResourcesMenu cmsResourceLinks={resourceLinks} />
                      )}
                      <CtaNavigation
                        links={cta_navigation}
                        smallScreenMenuClick={smallScreenMenuClick}
                      />
                      {!hasUtilityNav && process.env.GATSBY_STRAPI_LOCALE && <IntlMenu />}
                    </div>
                  )}
                </>
              )}
              {/* --------------------------------------------- | Mobile CTAs  */}
              {isMobileNavigation && (
                <>
                  {resourceLinks.length > 0 &&
                    authEnabled() &&
                    isAuthenticated() &&
                    isAuthorized && (
                      <Navigation
                        className={classNames(`cta_navigation`, styles.cta_navigation)}
                        links={resourceLinks}
                        onClick={smallScreenMenuClick}
                      />
                    )}
                  {process.env.GATSBY_STRAPI_LOCALE && (
                    <Navigation
                      newWindow="false"
                      className={classNames(`cta_navigation`, styles.cta_navigation)}
                      onClick={smallScreenMenuClick}
                      activeState={false}
                      intl
                    />
                  )}
                  <CtaNavigation
                    links={cta_navigation}
                    smallScreenMenuClick={smallScreenMenuClick}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

Header.propTypes = {
  /**
   * An array of links for the CTA
   */
  content_links: PropTypes.array,
  /**
   * Applies the correct site logo, based on CMS Header name
   */
  name: PropTypes.string.isRequired,
  /**
   * An array of main navigation links.
   * This prop is passed to the LinkList component
   */
  navigation: PropTypes.array,
  user: PropTypes.object,
  site_logo: PropTypes.object
};

Header.defaultProps = {};

export default Header;
